import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useMachine, useSelector } from '@xstate/react';

import { ibeSidebarAppointmentRead, ibeSidebarAppointmentStatusWrite } from 'actions/appointment/beSidebarAppointment.action';
import { deleteAllAppointmentsV3 } from 'actions/scheduler/deleteAllAppointments.action';
import { appointmentMachine } from 'bl/appointment/appointment.machine.bl';
import appointmentInfo from 'bl/appointment/appointmentInfo.bl';
import { LT_AP_SB } from 'bl/loggers.bl';
import HiddenDiv from 'components/core/HiddenDiv.component';
import FLAG from 'config/flag.singleton';
import DELAY from 'constants/delay.const';
import { APM_EVENT, APM_STATE } from 'constants/sidebar/sidebarAppointment.const';
import S from 'constants/string.const';
import MS from 'constants/time/ms.const';
import { usePrepareReadOnly, usePrepareReadWrite } from 'hooks/usePrepare.hook';
import { selectSidebarAppointmentId } from 'selectors/sidebar.selectors';
import asArray from 'utils/arrays/asArray.util';
import { firstOf } from 'utils/arrays/elements.util';
import noNilArray from 'utils/arrays/noNilArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { nowAsIso, nowAsTimestamp } from 'utils/date/now.util';
import delayed from 'utils/functions/delayed.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import isFn from 'utils/predicates/function/isFn.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';
import { isTimeNotBefore } from 'utils/predicates/time/compare.util';
import { concatTag as ct } from 'utils/strings/manipulate.util';

const L = logger(['AppointmentMachineProvider', LT_AP_SB]); // eslint-disable-line no-unused-vars

// noinspection JSCheckFunctionSignatures
export const MachineContext = createContext();

const COOLDOWN = MS.second;
const REJECT = Object.freeze({
  duplicate: 'already scheduled',
  missing: 'no appointment',
  stalest: 'older than current',
  throttle: 'too soon',
});

export const useApmRefresh = () => {
  let promise;

  const value = useContext(MachineContext);

  // noinspection UnnecessaryLocalVariableJS
  const apmRefresh = (options) => {
    const tag = fromAnyToTrimmed(options?.tag);
    const { appointmentId, appointment, force, receivedAt } = options ?? {};

    const timeout = value?.timeoutRef?.current ?? {};
    const isSame = appointmentId === timeout.aid;

    if (!appointment) {
      return { ...timeout, isSame, appointment, appointmentId, scheduled: false, reason: REJECT.missing };
    }

    if (!force && isSame && isTimeNotBefore(timeout.rat, receivedAt)) {
      return { ...timeout, isSame, appointment, appointmentId, scheduled: false, reason: REJECT.stalest };
    }

    if (!force && timeout?.tid) {
      return { ...timeout, isSame, appointment, appointmentId, scheduled: false, reason: REJECT.duplicate };
    }

    if ((!force || timeout.updating) && isSame && COOLDOWN > nowAsTimestamp() - timeout.uat) {
      return { ...timeout, isSame, appointment, appointmentId, scheduled: false, reason: REJECT.throttle };
    }

    timeout.uat = nowAsTimestamp();
    timeout.rat = receivedAt;
    timeout.aid = appointmentId;

    promise = (async () => {
      timeout.tid = delayed(DELAY.workaround, () => {
        L.info('apmRefresh()', tag, timeout, 'Stale appointment machine detected, refreshing with:', options, '...');

        const send = isFn(value?.actor?.send) ? value?.actor?.send : alwaysNoop;

        send({ type: APM_EVENT.refresh, appointment, input: { appointmentId } });
        send({ type: APM_EVENT.skip, appointment, appointmentId });

        timeout.tid = null;
      });
    })();

    return { ...timeout, appointment, appointmentId, force, scheduled: true, promise };
  };

  return apmRefresh;
};

const fromResultsToExpanded = (results) => {
  const d = results?.deleteResult?.value?.is?.ok;
  const s = results?.statusResult?.value?.is?.ok;
  const r = results?.refreshResult?.value?.is?.ok;

  return {
    deleteResult: results?.deleteResult,
    deleteIsOk: true === d,
    deleteNotOk: false === d,
    statusResult: results?.statusResult,
    statusIsOk: true === s,
    statusNotOk: false === s,
    refreshResult: results?.refreshResult,
    refreshIsOk: true === r,
    refreshNotOk: false === r,
  };
};

const InnerAppointmentMachineProvider = ({ appointmentId, children }) => {
  const timeIso = nowAsIso();

  const apmRefresh = useApmRefresh();
  const timeoutRef = useRef({});
  const awaitDeleteRef = useRef(Promise.resolve());
  const awaitStatusRef = useRef(Promise.resolve());
  const awaitRefreshRef = useRef(Promise.resolve());

  const {
    qj: appointment,
    qd,
    up: upStatus,
    isLoading: isLoadingStatus,
    isError: isErrorStatus,
  } = usePrepareReadWrite({
    reader: ibeSidebarAppointmentRead,
    writer: ibeSidebarAppointmentStatusWrite,
    rwOptions: { appointmentId },
  });

  const {
    up: upDelete,
    isLoading: isLoadingDelete,
    isError: isErrorDelete,
  } = usePrepareReadWrite({
    reader: ibeSidebarAppointmentRead,
    writer: deleteAllAppointmentsV3,
    rwOptions: { appointmentId },
    uOptions: { remove: true, refetch: false },
  });

  const updateBeStatus = useCallback(
    async (ev) => {
      const FTAG = 'updateBeStatus()';
      try {
        L.debug(FTAG, 'appointmentId:', appointmentId, 'ev:', ev);
        timeoutRef.current.updating = true;

        const rUpdate = upStatus({ status: ev?.status, appointmentId: ev?.appointmentId });
        awaitStatusRef.current = rUpdate;

        const { json: appointment, receivedAt } = await rUpdate;
        L.debug(FTAG, 'timeout:', timeoutRef?.current);

        const rRefresh = apmRefresh({ receivedAt, force: true, appointment, appointmentId, tag: 'updateBeStatus' });
        awaitRefreshRef.current = rRefresh?.promise;
      } catch (e) {
        L.warn(FTAG, e);
      } finally {
        timeoutRef.current.updating = false;
      }
    },
    [apmRefresh, appointmentId, upStatus],
  );

  const deleteBeAll = useCallback(() => {
    const result = upDelete(void 1);
    awaitDeleteRef.current = result;
    return result;
  }, [upDelete]);

  const bgn = fromAnyDateToIso(firstOf(noNilArray(appointment?.procedures))?.startTime);
  const input = { appointmentId, bgn, status: appointment?.status, updateBeStatus, deleteBeAll };
  // noinspection JSCheckFunctionSignatures
  const machine = useMachine(appointmentMachine, { input });
  const [apmSnap, send, apmActor] = machine ?? [];

  const apmSend = useCallback(
    async (...args) => {
      const FTAG = 'apmSend()';

      send(...args);

      const tag = fromAnyToTrimmed(args?.[0]?.tag);
      const type = args?.[0]?.type;

      const promises = [awaitDeleteRef.current, awaitStatusRef.current, awaitRefreshRef.current];
      if (APM_EVENT.tick !== type) L.debug(FTAG, tag, type, 'promises:', ...promises);

      const outcomes = await Promise.allSettled(promises);
      const [deleteResult, statusResult, refreshResult] = asArray(outcomes);
      const result = fromResultsToExpanded({ deleteResult, statusResult, refreshResult });

      if (APM_EVENT.tick !== type) L.debug(FTAG, tag, type, 'outcomes:', outcomes, 'result:', result);
      return result;
    },
    [send],
  );

  useEffect(() => {
    let stopped = false;

    const tick = () => {
      if (stopped) return;
      // noinspection JSCheckFunctionSignatures
      L.unignore('tick()', 'apmSend:', apmSend({ type: APM_EVENT.tick, cutoff: nowAsIso(), tag: 'tick()' }));
      delayed(DELAY.linger, tick);
    };

    tick(); // lift off

    return () => void (stopped = true);
  }, [apmActor, bgn, timeIso, apmSend]);

  useEffect(() => {
    if (!FLAG.loggerAppointmentMachine || isNotFn(apmActor?.subscribe)) return;

    const subscription = apmActor.subscribe(($) => void L.debug(`${S.ledger} apmActor.subscribe()`, $?.value, JSON.stringify($?.toJSON()), '<-', $));

    return () => subscription.unsubscribe();
  }, [apmActor]);

  const apmState = apmSnap?.value;
  if (APM_STATE.del === apmState) {
    L.info('The appointment with id:', appointmentId, 'was deleted.');
  } else if (appointmentId && timeoutRef?.current?.aid && appointmentId !== timeoutRef?.current?.aid) {
    // NOTE: precautions, don't want to trigger this on the initial load and the machine setup
    apmRefresh({ receivedAt: qd?.receivedAt, appointment, appointmentId, tag: 'appointmentId !== aid' });
  }

  const apInfo = appointmentInfo({ appointment });
  const value = { snapshot: apmSnap, apmSend, actor: apmActor, appointmentId, timeoutRef, apInfo };
  const loading = { isLoadingStatus, isErrorStatus, isLoadingDelete, isErrorDelete };

  const timeoutRefCurrent = timeoutRef.current;
  L.debug('()', ...L.O({ apmState, appointmentId, timeIso, timeoutRefCurrent, value, appointment, loading }));

  return (
    <MachineContext.Provider value={value}>
      <HiddenDiv
        tag="AppointmentMachineProvider"
        data-ap-id={String(appointmentId)}
        data-ap-bgn={String(bgn)}
        data-ap-status={String(appointment?.status)}
        data-apm-state={String(apmState)}
        data-to-aid={String(timeoutRef.current.aid)}
        data-to-tid={String(timeoutRef.current.tid)}
        data-to-uat={String(timeoutRef.current.uat)}
      />
      {children}
    </MachineContext.Provider>
  );
};

export const AppointmentMachineProvider = connect((state) => ({
  appointmentId: selectSidebarAppointmentId(state),
}))(InnerAppointmentMachineProvider);

export const useSidebarAppointmentId = () => {
  const value = useContext(MachineContext);
  return value?.appointmentId;
};

const selectState = ($) => $?.value;
const selectCtxConfirmInfo = ($) => ({
  fromState: $?.context?.fromState,
  toState: $?.context?.toState,
  fromStatus: $?.context?.fromStatus,
  toStatus: $?.context?.toStatus,
  isUnconfirmedTransition: $?.context?.isUnconfirmedTransition,
  isNoShowTransition: $?.context?.isNoShowTransition,
  isDeleteTransition: $?.context?.isDeleteTransition,
  isCancelingTransition: $?.context?.isCancelingTransition,
});
const selectCtxFuture = ($) => !!$?.context?.future;
const selectCtxPast = ($) => !!$?.context?.past;
const selectCtxStatus = ($) => $?.context?.appointmentStatus;

const useAutoRefreshingContext = () => {
  const apmRefresh = useApmRefresh();
  const value = useContext(MachineContext);

  const appointmentId = value?.appointmentId;
  const qr = usePrepareReadOnly(ibeSidebarAppointmentRead, { appointmentId });

  if (value?.timeoutRef?.current?.updating) return value;

  const receivedAt = qr?.data?.receivedAt;
  apmRefresh({ receivedAt, appointment: qr?.json, appointmentId, tag: 'useAutoRefreshingContext' });

  return value;
};

export const useApmSend = (tag) => {
  const FTAG = 'useApmSend()';

  const { apmSend: ogSend, snapshot, actor } = useContext(MachineContext) ?? {};
  // noinspection JSCheckFunctionSignatures
  const apmState = useSelector(actor, selectState);

  // noinspection UnnecessaryLocalVariableJS
  const ngSend = useCallback(
    (ev) => {
      const t = ct(tag, ev?.tag);

      if (!ev?.type) return void L.warn(FTAG, t, 'Invalid type in ev:', ev);
      if (!snapshot.can(ev)) return void L.warn(FTAG, t, 'Cannot transition with ev:', ev, 'from apmState:', apmState);

      L.debug(FTAG, t, 'Will transition with ev:', ev, 'from apmState:', apmState);
      return ogSend(ev);
    },
    [tag, snapshot, apmState, ogSend],
  );

  return ngSend;
};

export const useApmCan = () => {
  const snapshot = useContext(MachineContext)?.snapshot;

  return useCallback(
    (...args) => {
      try {
        return snapshot.can(...args);
      } catch (e) {
        L.warn('useApmCan()', e);
      }
    },
    [snapshot],
  );
};

export const useApmSelector = (selector) => {
  const actor = useAutoRefreshingContext()?.actor;
  return useSelector(actor, selector);
};

export const useApmConfirmation = () => useApmSelector(selectCtxConfirmInfo);
export const useApmState = () => useApmSelector(selectState);
export const useApmStatus = () => useApmSelector(selectCtxStatus);
export const useApmFuture = () => useApmSelector(selectCtxFuture);
export const useApmPast = () => useApmSelector(selectCtxPast);

export const useApInfo = () => useContext(MachineContext)?.apInfo;
export const useApInfoBgn = () => useContext(MachineContext)?.apInfo?.bgn;
export const useApInfoEnd = () => useContext(MachineContext)?.apInfo?.end;
export const useApInfoClientName = () => useContext(MachineContext)?.apInfo?.clientName;
